import { useEffect, useMemo, useRef, useState } from 'react';

import { isDefined } from '@spotify/confidence-core-react';
import {
  type CurrentUserGroupsQuery,
  GroupFragment,
  IdentityFragment,
  getError,
  getTypeOrNull,
  useCurrentUserGroupsQuery,
} from '@spotify/confidence-graphql';

const mergeCurrentUserGroups = (
  previous: CurrentUserGroupsQuery,
  next: CurrentUserGroupsQuery,
): CurrentUserGroupsQuery => {
  const previousGroups = getTypeOrNull(
    getTypeOrNull(previous.currentUser)?.user?.userGroups,
  );
  const currentUser = getTypeOrNull(next.currentUser);
  const user = currentUser?.user;
  const nextGroups = getTypeOrNull(user?.userGroups);
  if (!previousGroups || !currentUser || !user || !nextGroups) return next;

  const groupNames = new Set<string>();
  const memberships = [
    ...previousGroups.userGroups,
    ...nextGroups.userGroups,
  ].filter(({ group }) => {
    if (!group) return true;
    if (groupNames.has(group.name)) return false;
    groupNames.add(group.name);
    return true;
  });

  return {
    ...next,
    currentUser: {
      ...currentUser,
      user: {
        ...user,
        userGroups: {
          ...nextGroups,
          userGroups: memberships,
        },
      },
    },
  };
};

const identityNameFromGroup = (group: GroupFragment): string =>
  group.identity?.name ?? group.name.replace('groups/', 'identities/g');

export const useCurrentUserGroups = (options?: {
  skip?: boolean;
}): {
  isLoading: boolean;
  error: Error | null | undefined;
  hasUnavailableGroups: boolean;
  groups: GroupFragment[];
  identities: IdentityFragment[];
} => {
  const {
    loading,
    data,
    error: networkError,
    fetchMore,
  } = useCurrentUserGroupsQuery({
    fetchPolicy: 'cache-first',
    skip: options?.skip,
  });

  const currentUser = getTypeOrNull(data?.currentUser);
  const userGroups = currentUser?.user?.userGroups;
  const userGroupsResponse = getTypeOrNull(userGroups);
  const groupsError =
    getError(userGroups) ??
    (!loading && (!currentUser?.user || !userGroupsResponse)
      ? new Error('Could not load group memberships')
      : undefined);
  const appError = getError(data?.currentUser);

  const nextPageToken = userGroupsResponse?.nextPageToken;
  const [paginationError, setPaginationError] = useState<Error>();
  const requestedPageToken = useRef<string>();

  useEffect(() => {
    if (
      options?.skip ||
      !nextPageToken ||
      paginationError ||
      requestedPageToken.current === nextPageToken
    )
      return;
    requestedPageToken.current = nextPageToken;
    fetchMore({
      variables: { pageToken: nextPageToken },
      updateQuery: (previous, { fetchMoreResult }) =>
        mergeCurrentUserGroups(previous, fetchMoreResult),
    }).catch((error: unknown) =>
      setPaginationError(
        error instanceof Error ? error : new Error(String(error)),
      ),
    );
  }, [options?.skip, nextPageToken, paginationError, fetchMore]);

  const groups = useMemo<GroupFragment[]>(
    () =>
      userGroupsResponse?.userGroups.map(ug => ug.group).filter(isDefined) ??
      [],
    [userGroupsResponse],
  );
  const identities = useMemo<IdentityFragment[]>(
    () =>
      groups.map(group => ({
        __typename: 'IamV1Identity',
        name: identityNameFromGroup(group),
        displayName: group.displayName,
        group,
      })),
    [groups],
  );

  return {
    isLoading:
      !options?.skip &&
      (loading || (Boolean(nextPageToken) && !paginationError)),
    error: appError ?? networkError ?? groupsError ?? paginationError,
    hasUnavailableGroups:
      userGroupsResponse?.userGroups.some(({ group }) => !group) ?? false,
    groups,
    identities,
  };
};
